import {
    get,
    post,
    trash,
    ServerErrors,
    ResponseType,
    sortCallback,
    DeploymentNodeType,
    put,
} from '@devtron-labs/devtron-fe-common-lib'
import moment from 'moment'
import { Routes, Moment12HourFormat, SourceTypeMap, NO_COMMIT_SELECTED } from '../../config'
import { createGitCommitUrl, getAPIOptionsWithTriggerTimeout, handleUTCTime, ISTTimeModal } from '../common'
import { History } from './details/cicdHistory/types'
import { AppDetails, ArtifactsCiJob, EditAppRequest, AppMetaInfo } from './types'
import { DeploymentWithConfigType } from './details/triggerView/types'

const stageMap = {
    PRECD: 'PRE',
    CD: 'DEPLOY',
    POSTCD: 'POST',
    APPROVAL: 'APPROVAL',
}

export const getAppList = (request, options?) => {
    return post(Routes.APP_LIST, request, options)
}

export function getCITriggerInfo(params: { envId: number | string; ciArtifactId: number | string }) {
    const URL = `${Routes.APP}/material-info/${params.envId}/${params.ciArtifactId}`
    return get(URL)
}

export function getCITriggerInfoModal(
    params: { envId: number | string; ciArtifactId: number | string },
    commit: string,
) {
    return getCITriggerInfo(params).then((response) => {
        let materials = response?.result?.ciMaterials || []
        materials = materials.map((mat) => {
            return {
                id: mat.id,
                gitMaterialName: mat.gitMaterialName || '',
                gitMaterialId: mat.gitMaterialId || 0,
                gitURL: mat.url || '',
                type: mat.type || '',
                value: mat.value || '',
                active: mat.active || false,
                history: mat.history.map((hist, index) => {
                    return {
                        commitURL: mat.url ? createGitCommitUrl(mat.url, hist.Commit) : '',
                        commit: hist.Commit || '',
                        author: hist.Author || '',
                        date: hist.Date ? ISTTimeModal(hist.Date, false) : '',
                        message: hist.Message || '',
                        changes: hist.Changes || [],
                        showChanges: index === 0,
                        webhookData: hist.WebhookData,
                    }
                }),
                isSelected:
                    mat.history.find((h) =>
                        mat.type != SourceTypeMap.WEBHOOK ? h.Commit === commit : h.WebhookData.id == commit,
                    ) || false,
                lastFetchTime: mat.lastFetchTime || '',
            }
        })
        if (materials.length > 0 && !materials.find((mat) => mat.isSelected)) {
            materials[0].isSelected = true
        }
        return {
            code: response.code,
            result: {
                materials,
                triggeredByEmail: response.result.triggeredByEmail || '',
                lastDeployedTime: response.result.lastDeployedTime
                    ? handleUTCTime(response.result.lastDeployedTime, false)
                    : '',
                environmentName: response.result.environmentName || '',
                environmentId: response.result.environmentId || 0,
                appName: response.result.appName || '',
                appReleaseTags: response?.result?.imageTaggingData?.appReleaseTags,
                imageComment: response?.result?.imageTaggingData?.imageComment,
                imageReleaseTags: response?.result?.imageTaggingData?.imageReleaseTags,
                image: response?.result?.image,
                tagsEditable: response?.result?.imageTaggingData?.tagsEditable,
            },
        }
    })
}

export function deleteResource({ appName, env, name, kind, group, namespace, version, appId, envId }) {
    if (!group) {
        group = ''
    }
    const URL = `${Routes.APPLICATIONS}/${appName}-${env}/resource?name=${name}&namespace=${namespace}&resourceName=${name}&version=${version}&group=${group}&kind=${kind}&force=true&appId=${appId}&envId=${envId}`
    return trash(URL)
}

interface AppDetailsResponse extends ResponseType {
    result?: AppDetails
}

interface AppMetaInfoResponse extends ResponseType {
    result?: AppMetaInfo
}

export interface ArtifactCiJobResponse extends ResponseType {
    result?: ArtifactsCiJob
}

export function fetchAppDetails(appId: number | string, envId: number | string): Promise<AppDetailsResponse> {
    return get(`${Routes.APP_DETAIL}?app-id=${appId}&env-id=${envId}`)
}

export function fetchAppDetailsInTime(
    appId: number | string,
    envId: number | string,
    reloadTimeOut: number,
    signal?: AbortSignal,
): Promise<AppDetailsResponse> {
    return get(`${Routes.APP_DETAIL}/v2?app-id=${appId}&env-id=${envId}`, { timeout: reloadTimeOut, signal })
}

export function fetchResourceTreeInTime(
    appId: number | string,
    envId: number | string,
    reloadTimeOut: number,
    signal?: AbortSignal,
): Promise<AppDetailsResponse> {
    return get(`${Routes.APP_DETAIL}/resource-tree?app-id=${appId}&env-id=${envId}`, {
        timeout: reloadTimeOut,
        signal,
    })
}

export function getEvents(pathParams) {
    const URL = `${Routes.APPLICATIONS}/${pathParams.appName}-${pathParams.env}/events?resourceNamespace=${pathParams.resourceNamespace}&resourceUID=${pathParams.uid}&resourceName=${pathParams.resourceName}`
    return URL
}

export function getCITriggerDetails(params: {
    appId: number | string
    pipelineId: number | string
}): Promise<{ code: number; status: string; triggerDetails: any }> {
    const URL = `${Routes.APP}/${params.appId}/ci-pipeline/${params.pipelineId}/workflow/last`
    return get(URL).then((response) => {
        if (response.result && !response.errors) {
            return {
                code: response.code,
                status: response.status,
                triggerDetails: {
                    id: response.result.id || 0,
                    startedOn: response.result.startedOn,
                    finishedOn: response.result.finishedOn,
                    status: response.result.status || '',
                    name: response.result.name || '',
                    namespace: response.result.namespace || '',
                    podStatus: response.result.podStatus || '',
                    message: response.result.message || '',
                    gitTriggers: response.result.gitTriggers
                        ? gitTriggersModal(response.result.gitTriggers, response.result.ciMaterials)
                        : [],
                },
            }
        }
        throw new ServerErrors({ code: response.code, errors: response.errors })
    })
}

interface CIHistoricalStatus extends ResponseType {
    result?: History
}

export const getCIHistoricalStatus = (params): Promise<CIHistoricalStatus> => {
    const URL = `${Routes.APP}/${params.appId}/ci-pipeline/${params.pipelineId}/workflow/${params.buildId}`
    return get(URL)
}

export const getTagDetails = (params) => {
    const URL = `${Routes.IMAGE_TAGGING}/${params.pipelineId}/${params.artifactId}`
    return get(URL)
}

const gitTriggersModal = (triggers, materials) => {
    const ids = Object.keys(triggers)
    return ids.map((key) => {
        const material = materials.find((mat) => mat.id === Number(key))
        return {
            id: key,
            gitMaterialName: material?.gitMaterialName || '',
            changes: triggers[key].Changes,
            commit: triggers[key].Commit,
            author: triggers[key].Author,
            message: triggers[key].Message,
            url: material?.url || '',
            date: triggers[key].Date ? ISTTimeModal(triggers[key].Date) : '',
        }
    })
}

const processMaterialHistoryAndSelectionError = (material) => {
    const data = {
        isMaterialSelectionError: true,
        materialSelectionErrorMsg: NO_COMMIT_SELECTED,
        history: [],
    }
    if (material.history) {
        let selectedIndex = -1
        for (let index = 0; index < material.history.length; index++) {
            const history = material.history[index]
            if (selectedIndex === -1 && !history.Excluded) {
                selectedIndex = index
            }
            data.history.push({
                commitURL: material.gitMaterialUrl ? createGitCommitUrl(material.gitMaterialUrl, history.Commit) : '',
                changes: history.Changes || [],
                author: history.Author,
                message: history.Message,
                date: history.Date ? moment(history.Date).format(Moment12HourFormat) : '',
                commit: history?.Commit,
                isSelected: index === selectedIndex,
                showChanges: false,
                webhookData: history.WebhookData
                    ? {
                          id: history.WebhookData.id,
                          eventActionType: history.WebhookData.eventActionType,
                          data: history.WebhookData.data,
                      }
                    : null,
                excluded: history.Excluded,
            })
        }
        if (selectedIndex >= 0) {
            data.isMaterialSelectionError = false
            data.materialSelectionErrorMsg = ''
        }
    }
    return data
}

const processCIMaterialResponse = (response) => {
    if (Array.isArray(response?.result)) {
        const sortedCIMaterials = response.result.sort((a, b) => sortCallback('id', a, b))
        return sortedCIMaterials.map((material, index) => {
            return {
                ...material,
                isSelected: index == 0,
                gitURL: material.gitMaterialUrl || '',
                lastFetchTime: material.lastFetchTime ? ISTTimeModal(material.lastFetchTime, true) : '',
                isMaterialLoading: false,
                showAllCommits: false,
                ...processMaterialHistoryAndSelectionError(material),
            }
        })
    }

    return []
}

export const getCIMaterialList = (params, abortSignal: AbortSignal) => {
    let url = `${Routes.CI_CONFIG_GET}/${params.pipelineId}/material`
    if (params.materialId) {
        url += `/${params.materialId}${params.showExcluded ? '?showAll=true' : ''} `
    }
    return get(url, {
        signal: abortSignal,
    }).then((response) => {
        const materials = processCIMaterialResponse(response)
        return {
            code: response.code,
            status: response.status,
            result: materials,
        }
    })
}

export function extractImage(image: string): string {
    return image ? image.split(':').pop() : ''
}

export const cancelCiTrigger = (params, isForceAbort) => {
    const URL = `${Routes.CI_CONFIG_GET}/${params.pipelineId}/workflow/${params.workflowId}?forceAbort=${isForceAbort}`
    return trash(URL)
}

export const cancelPrePostCdTrigger = (pipelineId, workflowRunner) => {
    const URL = `${Routes.CD_CONFIG}/${pipelineId}/workflowRunner/${workflowRunner}`
    return trash(URL)
}

export const getRecentDeploymentConfig = (appId: number, pipelineId: number) => {
    return get(`${Routes.RECENT_DEPLOYMENT_CONFIG}/${appId}/${pipelineId}`)
}

export const getLatestDeploymentConfig = (appId: number, pipelineId: number) => {
    return get(`${Routes.LATEST_DEPLOYMENT_CONFIG}/${appId}/${pipelineId}`)
}

export const getSpecificDeploymentConfig = (appId: number, pipelineId: number, wfrId: number) => {
    return get(`${Routes.SPECIFIC_DEPLOYMENT_CONFIG}/${appId}/${pipelineId}/${wfrId}`)
}

export const triggerCINode = (request) => {
    const URL = `${Routes.CI_PIPELINE_TRIGGER}`
    return post(URL, request)
}

export const triggerCDNode = (
    pipelineId: any,
    ciArtifactId: any,
    appId: string,
    stageType: DeploymentNodeType,
    deploymentWithConfig?: string,
    wfrId?: number,
) => {
    const request = {
        pipelineId: parseInt(pipelineId),
        appId: parseInt(appId),
        ciArtifactId: parseInt(ciArtifactId),
        cdWorkflowType: stageMap[stageType],
    }

    if (deploymentWithConfig) {
        request['deploymentWithConfig'] =
            deploymentWithConfig === DeploymentWithConfigType.LAST_SAVED_CONFIG
                ? deploymentWithConfig
                : DeploymentWithConfigType.SPECIFIC_TRIGGER_CONFIG

        if (deploymentWithConfig !== DeploymentWithConfigType.LAST_SAVED_CONFIG) {
            request['wfrIdForDeploymentWithSpecificTrigger'] = wfrId
        }
    }
    const options = getAPIOptionsWithTriggerTimeout()

    return post(Routes.CD_TRIGGER_POST, request, options)
}

export const triggerBranchChange = (appIds: number[], envId: number, value: string) => {
    const request = {
        appIds,
        environmentId: envId,
        value,
    }
    return put(Routes.CI_PIPELINE_SOURCE_BULK_PATCH, request)
}

export const getPrePostCDTriggerStatus = (params) => {
    const URL = `${Routes.APP}/cd-pipeline/workflow/status/${params.appId}/${params.environmentId}/${params.pipelineId}`
    return get(URL)
}

export const getWorkflowStatus = (appId: string) => {
    const URL = `${Routes.APP_WORKFLOW_STATUS}/${appId}/${Routes.APP_LIST_V2}`
    return get(URL)
}

export const getCIPipelines = (appId, filteredEnvIds?: string, callback?: (...args) => void) => {
    let filteredEnvParams = ''
    if (filteredEnvIds) {
        filteredEnvParams = `?envIds=${filteredEnvIds}`
    }
    const URL = `${Routes.APP}/${appId}/${Routes.APP_CI_PIPELINE}${filteredEnvParams}`
    return get(URL).catch((error) => {
        if (callback) {
            callback(error)
        }

        return {
            code: error.code,
            status: error.status,
            result: [],
        }
    })
}

export function refreshGitMaterial(gitMaterialId: string, abortSignal: AbortSignal) {
    const URL = `${Routes.REFRESH_MATERIAL}/${gitMaterialId}`
    return get(URL, {
        signal: abortSignal,
    }).then((response) => {
        return {
            code: response.code,
            result: {
                message: response.result.message || '',
                errorMsg: response.result.errorMsg || '',
                lastFetchTime: response.result.lastFetchTime ? handleUTCTime(response.result.lastFetchTime, true) : '',
            },
        }
    })
}

export function getGitMaterialByCommitHash(gitMaterialId: string, commitHash: string) {
    return get(`${Routes.COMMIT_INFO}/${gitMaterialId}/${commitHash}`)
}

export const getCDTriggerStatus = (appId) => {
    const URL = `${Routes.CD_TRIGGER_STATUS}?app-id=${appId}`
    return get(URL, { timeout: 3 * 60000 }).then((response) => {
        return response.result
            ? response?.result?.map((status) => {
                  return {
                      ciPipelineId: status.ciPipelineId,
                      ciPipelineName: status.ciPipelineName,
                      cdPipelineId: status.cdPipelineId,
                      cdPipelineName: status.cdPipelineName,
                      status: status.status,
                      environmentName: status.environmentName,
                      materialInfo: status.materialInfo,
                      lastDeployedBy: status.lastDeployedBy,
                      lastDeployedTime: status.lastDeployedTime ? handleUTCTime(status.lastDeployedTime, true) : '',
                  }
              })
            : []
    })
}

export function getTriggerHistory(pipelineId, params) {
    const URL = `${Routes.CI_CONFIG_GET}/${pipelineId}/workflows?offset=${params.offset}&size=${params.size}`
    return get(URL)
}
export function setImageTags(request, pipelineId: number, artifactId: number) {
    return post(`${Routes.IMAGE_TAGGING}/${pipelineId}/${artifactId}`, request)
}

export function getImageTags(pipelineId: number, artifactId: number) {
    return get(`${Routes.IMAGE_TAGGING}/${pipelineId}/${artifactId}`)
}

function handleTime(ts: string) {
    let timestamp = ''
    try {
        if (ts && ts.length) {
            const date = moment(ts).utc(true).subtract(5, 'hours').subtract(30, 'minutes')
            timestamp = date.format('ddd DD MMM YYYY HH:mm:ss')
        }
    } catch (error) {
        console.error('Error Parsing Date:', ts)
    }
    return timestamp
}

export function handleTimeWithOffset(ts: string) {
    let timestamp = ''
    try {
        if (ts && ts.length) {
            const date = moment(ts).add(5, 'hours').add(30, 'minutes')
            timestamp = date.format('ddd DD MMM YYYY HH:mm:ss')
        }
    } catch (error) {
        console.error('Error Parsing Date:', ts)
    }
    return timestamp
}

export function getArtifact(pipelineId, workflowId) {
    const URL = `${Routes.CI_CONFIG_GET}/${pipelineId}/artifacts/${workflowId}`
    return get(URL).then((response) => {
        return response
    })
}

export function getArtifactForJobCi(pipelineId, workflowId): Promise<ArtifactCiJobResponse> {
    const URL = `${Routes.CI_CONFIG_GET}/${pipelineId}/workflow/${workflowId}/ci-job/artifacts`
    return get(URL)
}

export function getNodeStatus({ appName, envName, version, namespace, group, kind, name }) {
    if (!group) {
        group = ''
    }
    return get(
        `api/v1/applications/${appName}-${envName}/resource?version=${version}&namespace=${namespace}&group=${group}&kind=${kind}&resourceName=${name}`,
    )
}

export function getAppMetaInfo(appId: number): Promise<AppMetaInfoResponse> {
    return get(`${Routes.APP_META_INFO}/${appId}`)
}

export function getHelmAppMetaInfo(appId: string): Promise<AppMetaInfoResponse> {
    return get(`${Routes.HELM_APP_META_INFO}/${appId}`)
}

export function getHelmAppOverviewInfo(installedAppId: string): Promise<AppMetaInfoResponse> {
    return get(`${Routes.HELM_APP_OVERVIEW}?installedAppId=${installedAppId}`)
}

export const editApp = (request: EditAppRequest): Promise<ResponseType> => {
    return post(Routes.APP_EDIT, request)
}

export const getIngressServiceUrls = (params: {
    appId?: string
    envId: string
    installedAppId?: string
}): Promise<ResponseType> => {
    const urlParams = Object.entries(params).map(([key, value]) => {
        if (!value) {
            return
        }
        return `${key}=${value}`
    })
    return get(`${Routes.INGRESS_SERVICE_MANIFEST}?${urlParams.filter((s) => s).join('&')}`)
}

export function getManualSync(params: { appId: string; envId: string }): Promise<ResponseType> {
    return get(`${Routes.MANUAL_SYNC}/${params.appId}/${params.envId}`)
}
